// RenameClientDialog — modal pro přejmenování klienta.
// Designově laděný s ConfirmDialog (stejný layout, ikona, tlačítka).
// User vyplní nové jméno do inputu, vidí varování že přejmenování ovlivní
// historické záznamy. Tréninkový profil zůstane spárovaný přes UUID.

import React, { useEffect, useState, useRef } from "react";
import useEscClose from "../lib/useEscClose.js";
import { createPortal } from "react-dom";
import { AlertTriangle, X, UserCircle } from "lucide-react";

export default function RenameClientDialog({
  open,
  oldName,
  onConfirm,
  onCancel,
  loading = false,
}) {
  useEscClose(onCancel, !!open); // Esc = Zrusit (jen kdyz je dialog otevreny)
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setNewName(oldName || "");
      setError("");
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [open, oldName]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onCancel?.();
      if (e.key === "Enter" && !loading) handleConfirm();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, newName]);

  const handleConfirm = () => {
    const trimmed = newName.trim();
    if (!trimmed) { setError("Jméno nesmí být prázdné."); return; }
    if (trimmed === oldName) { setError("Nové jméno je stejné jako staré."); return; }
    if (trimmed.length < 3) { setError("Jméno musí mít aspoň 3 znaky."); return; }
    setError("");
    onConfirm?.(trimmed);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-dark/50 backdrop-blur-sm animate-in fade-in duration-150 pb-[max(1rem,env(safe-area-inset-bottom))]"
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onCancel?.(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header se stejným stylem jako ConfirmDialog */}
        <div className="flex items-start gap-4 mb-4">
          <div className="p-3 rounded-xl flex-shrink-0 bg-amber-100 text-amber-600">
            <UserCircle className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 leading-tight">
              Přejmenovat klienta
            </h3>
            <div className="text-sm text-slate-600 mt-1 leading-relaxed">
              Změní zobrazované jméno klienta. Tréninkový profil zůstane spárovaný (přes UUID).
            </div>
          </div>
          <button
            onClick={() => !loading && onCancel?.()}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            aria-label="Zavřít"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Staré jméno */}
        <div className="mb-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">Aktuální jméno</div>
          <div className="text-sm font-semibold text-slate-700 break-words">{oldName || "(neznámé)"}</div>
        </div>

        {/* Nové jméno */}
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Nové jméno</label>
          <input
            ref={inputRef}
            type="text"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); if (error) setError(""); }}
            disabled={loading}
            placeholder="Např. Alena Bryndová Nová"
            className={`w-full text-base sm:text-sm border-2 rounded-xl px-3 py-2.5 outline-none transition-colors ${
              error
                ? "border-rose-300 focus:border-rose-500 bg-rose-50/30"
                : "border-slate-200 focus:border-amber-400 bg-white"
            }`}
          />
          {error && (
            <div className="mt-1.5 text-xs text-rose-700 inline-flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
              {error}
            </div>
          )}
        </div>

        {/* Varování + co se změní */}
        <div className="mt-4 px-3 py-2.5 bg-rose-50 border border-rose-300 rounded-xl text-xs text-rose-900 leading-relaxed">
          <div className="font-bold mb-1 inline-flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> POZOR
          </div>
          <div>
            Přejmenovat smí owner a CSM, sales jen nový kontakt. Změna jména může rozhodit párování tréninků a faktur z FAPI (párují se podle jména), pak se klientce do dalšího běhu může ukázat 0 tréninků a 0 zaplacených faktur. Používej jen při skutečné nutnosti (oprava překlepu, změna příjmení).
          </div>
        </div>
        <div className="mt-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 leading-relaxed">
          <div className="font-bold mb-1 inline-flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Co se změní
          </div>
          <ul className="space-y-0.5 list-disc list-inside pl-1">
            <li>Jméno klienta v PG (primary source od 29.5.2026).</li>
            <li>Tréninkový profil zůstane spárovaný přes UUID - cviky, log, poznámky se neztratí.</li>
            <li>Historie přejmenování se uloží pro dohledání pod původním jménem.</li>
          </ul>
        </div>

        {/* Actions - stejný styl jako ConfirmDialog */}
        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="btn btn-secondary"
          >
            Zrušit
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || !newName.trim() || newName.trim() === oldName}
            className="btn btn-primary"
          >
            {loading && (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            Přejmenovat
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
